import React, { useEffect, useState } from "react";
import { Mail, MessageSquareText, CheckSquare, CalendarDays, Sparkles, FolderClosed, Image as ImageIcon, FileText, Search, Mic, Hexagon, Send, Reply, ArrowRight, Lock } from "lucide-react";

// Four "frames" — each rendered as a React mock of the real OS surface.
// Auto-advances every ~6 seconds.
const FRAMES = [
  { id: "dashboard", label: "AI Chief of Staff (AI COS) · AI greeting" },
  { id: "mail",      label: "Mail · AI Insight panel" },
  { id: "files",     label: "Files · Auto-organize" },
  { id: "start",     label: "Start Menu · Grouped apps" },
];

export default function HeroDemo() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive((i) => (i + 1) % FRAMES.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      data-testid="hero-demo"
      className="relative w-full aspect-[16/10] max-h-[440px]"
      style={{
        background: "linear-gradient(180deg, #0a0f1e, #050813)",
        borderRadius: 12,
        border: "1px solid rgba(167,139,250,0.28)",
        boxShadow:
          "0 40px 100px rgba(0,0,0,0.55), 0 0 0 1px rgba(167,139,250,0.18) inset, 0 0 60px rgba(124,58,237,0.15)",
        overflow: "hidden",
      }}
    >
      {/* Browser chrome */}
      <div
        className="flex items-center gap-2 px-3 py-2 border-b border-white/8"
        style={{ background: "rgba(8,11,22,0.95)" }}
      >
        <span className="w-2.5 h-2.5 rounded-full bg-[#f87171]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#fbbf24]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#34d399]" />
        <div
          className="flex-1 mx-2 px-3 py-1 mono text-[10px] text-white/55 flex items-center gap-1.5"
          style={{
            background: "rgba(12,18,36,0.85)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 4,
          }}
        >
          <Lock size={9} className="text-[#34d399]" />
          hexawebos.com
        </div>
        <div className="mono text-[9px] uppercase tracking-[0.22em] text-[#a78bfa]/80">
          live preview
        </div>
      </div>

      {/* Frames */}
      <div className="relative w-full" style={{ height: "calc(100% - 36px)" }}>
        {FRAMES.map((f, i) => (
          <div
            key={f.id}
            className="absolute inset-0 transition-opacity duration-700"
            style={{
              opacity: i === active ? 1 : 0,
              pointerEvents: i === active ? "auto" : "none",
              transform: i === active ? "scale(1)" : "scale(1.02)",
              transition: "opacity 700ms ease, transform 6s ease",
            }}
          >
            {i === 0 && <FrameDashboard />}
            {i === 1 && <FrameMail />}
            {i === 2 && <FrameFiles />}
            {i === 3 && <FrameStart />}
          </div>
        ))}
      </div>

      {/* Progress dots + caption */}
      <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {FRAMES.map((f, i) => (
          <button
            key={f.id}
            onClick={() => setActive(i)}
            className="transition-all"
            style={{
              width: i === active ? 22 : 6,
              height: 6,
              borderRadius: 3,
              background: i === active ? "linear-gradient(120deg, #a78bfa, #60a5fa)" : "rgba(255,255,255,0.22)",
              boxShadow: i === active ? "0 0 12px rgba(167,139,250,0.55)" : "none",
            }}
            aria-label={f.label}
          />
        ))}
      </div>
    </div>
  );
}

/* ------------------------- Frame 1 — Dashboard ------------------------- */
function FrameDashboard() {
  return (
    <div className="absolute inset-0 px-7 py-6 flex flex-col gap-3">
      <div
        className="text-white"
        style={{
          fontFamily: '"Montserrat", "Inter", system-ui, sans-serif',
          fontWeight: 700,
          fontSize: "clamp(20px, 2.6vw, 32px)",
          lineHeight: 1.1,
        }}
      >
        <span style={{
          background: "linear-gradient(120deg, #a78bfa, #60a5fa)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}>
          Good morning, Vedant
        </span>
        <span className="ml-2">👋</span>
      </div>
      <div className="text-sm text-white/75">
        You have{" "}
        <span className="font-medium text-[#34d399]">3 tasks</span>,{" "}
        <span className="font-medium text-[#60a5fa]">2 meetings</span>, and{" "}
        <span className="font-medium text-[#a78bfa]">5 unread emails</span>.
      </div>

      {/* AI command bar */}
      <div
        className="mt-2 px-3 py-2.5 flex items-center gap-2"
        style={{
          background: "rgba(139,92,246,0.06)",
          border: "1px solid rgba(167,139,250,0.25)",
          borderRadius: 4,
          clipPath: "polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))",
        }}
      >
        <Sparkles size={12} className="text-[#a78bfa]" />
        <div className="flex-1 text-sm text-white/55 flex items-center">
          Ask HEXA anything…
          <span className="ml-0.5 w-0.5 h-3.5 bg-[#a78bfa]" style={{ animation: "hexa-caret 1s steps(2) infinite" }} />
        </div>
        <Mic size={13} className="text-white/45" />
      </div>

      <div className="grid grid-cols-3 gap-2 mt-1">
        {[
          { label: "Emails", value: 5, sub: "unread", color: "#60a5fa" },
          { label: "Tasks", value: 3, sub: "pending", color: "#34d399" },
          { label: "Files", value: 27, sub: "total", color: "#a78bfa" },
        ].map((m) => (
          <div
            key={m.label}
            className="p-2.5"
            style={{
              background: `linear-gradient(160deg, ${m.color}22, ${m.color}08)`,
              border: `1px solid ${m.color}33`,
              clipPath: "polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))",
            }}
          >
            <div className="mono text-[9px] uppercase tracking-[0.22em] text-white/55">{m.label}</div>
            <div className="text-white text-xl font-medium mt-1">{m.value}</div>
            <div className="mono text-[9px] uppercase tracking-[0.22em] text-white/35">{m.sub}</div>
          </div>
        ))}
      </div>

      <div className="mt-auto mono text-[9px] uppercase tracking-[0.28em] text-white/30">
        // dashboard · narrated greeting plays once
      </div>

      <style>{`@keyframes hexa-caret { to { opacity: 0; } }`}</style>
    </div>
  );
}

/* --------------------------- Frame 2 — Mail --------------------------- */
function FrameMail() {
  return (
    <div className="absolute inset-0 flex">
      {/* Mail list */}
      <div className="w-[38%] border-r border-white/8 p-3 flex flex-col gap-1.5">
        <div className="mono text-[9px] uppercase tracking-[0.22em] text-white/45 mb-1">// inbox</div>
        {[
          { from: "@carol", subj: "Meeting invite: Demo sync", active: true, color: "#60a5fa" },
          { from: "@rahul", subj: "Q4 report — your review", color: "#a78bfa" },
          { from: "@hexa-ceo", subj: "Roadmap input?", color: "#34d399" },
        ].map((m, i) => (
          <div
            key={i}
            className="px-2 py-1.5 rounded text-[11px]"
            style={{
              background: m.active ? "rgba(96,165,250,0.10)" : "transparent",
              border: m.active ? "1px solid rgba(96,165,250,0.35)" : "1px solid transparent",
            }}
          >
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
              <span className="mono text-[10px] text-white/75">{m.from}</span>
            </div>
            <div className={`mt-0.5 truncate ${m.active ? "text-white" : "text-white/65"}`}>{m.subj}</div>
          </div>
        ))}
      </div>

      {/* AI Insight panel */}
      <div className="flex-1 p-3 flex flex-col gap-2 overflow-hidden">
        <div className="flex items-center justify-between">
          <div className="mono text-[9px] uppercase tracking-[0.22em] text-white/45 flex items-center gap-1.5">
            <Sparkles size={10} className="text-[#ff7a2e]" /> ai insight
          </div>
          <span
            className="text-[9px] mono uppercase tracking-[0.22em] px-1.5 py-0.5 rounded-full"
            style={{ background: "rgba(96,165,250,0.18)", color: "#60a5fa" }}
          >
            meeting
          </span>
        </div>
        <div>
          <div className="mono text-[9px] uppercase tracking-[0.22em] text-white/40">Purpose</div>
          <div className="text-[12px] text-white/85">Meeting invitation for &lsquo;Demo sync&rsquo; on June 15 at 10:00 AM</div>
        </div>
        <div>
          <div className="mono text-[9px] uppercase tracking-[0.22em] text-white/40">Action</div>
          <div className="text-[12px] text-white/85">Accept the invite and join via HEXA Meetings</div>
        </div>
        <div className="flex flex-wrap gap-1 mt-1">
          <span className="text-[10px] px-1.5 py-0.5 rounded-full mono" style={{ background: "rgba(96,165,250,0.15)", color: "#60a5fa" }}>📅 June 15</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full mono" style={{ background: "rgba(167,139,250,0.15)", color: "#a78bfa" }}>👤 Carol</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded mono" style={{ background: "rgba(52,211,153,0.12)", color: "#34d399" }}>✓ Join meeting</span>
        </div>
        <div
          className="mt-1 rounded text-[11px] p-2"
          style={{
            background: "rgba(12,18,36,0.7)",
            border: "1px solid rgba(167,139,250,0.32)",
          }}
        >
          <div className="mono text-[9px] uppercase tracking-[0.22em] text-[#a78bfa] mb-1">auto-draft</div>
          <div className="text-white/85 leading-snug" style={{ animation: "hexa-typewriter 5s steps(40) infinite" }}>
            Hi Carol, Thanks for the invite — I&rsquo;ll join the Demo sync on June 15 at 10:00 AM via HEXA Meetings. Looking forward.
          </div>
          <div className="flex gap-1 mt-1.5">
            <span className="text-[9px] mono px-2 py-0.5 rounded" style={{ background: "linear-gradient(120deg,#8b5cf6,#3b82f6)", color: "#fff" }}>SEND</span>
            <span className="text-[9px] mono px-2 py-0.5 rounded border border-white/15 text-white/65">EDIT</span>
          </div>
        </div>
      </div>
      <style>{`@keyframes hexa-typewriter { 0% { opacity: 0.5; } 30%,100% { opacity: 1; } }`}</style>
    </div>
  );
}

/* --------------------------- Frame 3 — Files -------------------------- */
function FrameFiles() {
  const folders = [
    { name: "Images", icon: ImageIcon, tint: "#a78bfa", count: 14 },
    { name: "Audio", icon: Mic, tint: "#f59e0b", count: 3 },
    { name: "Video", icon: FolderClosed, tint: "#3b82f6", count: 6 },
    { name: "Workspace", icon: FolderClosed, tint: "#fbbf24", count: 22 },
  ];
  return (
    <div className="absolute inset-0 p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="mono text-[9px] uppercase tracking-[0.22em] text-white/45">// home</div>
        <div className="text-[10px] text-[#34d399] mono uppercase tracking-[0.22em] flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#34d399] animate-pulse" />
          shield · scanning
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {folders.map((f) => {
          const Icon = f.icon;
          return (
            <div
              key={f.name}
              className="flex flex-col items-center gap-1.5 p-3 rounded"
              style={{
                background: `linear-gradient(160deg, ${f.tint}18, ${f.tint}05)`,
                border: `1px solid ${f.tint}33`,
              }}
            >
              <Icon size={28} strokeWidth={1.3} color={f.tint} />
              <div className="text-[11px] text-white/85">{f.name}</div>
              <div className="mono text-[9px] text-white/40">{f.count} items</div>
            </div>
          );
        })}
      </div>
      <div
        className="mt-2 p-3 flex items-center gap-3"
        style={{
          background: "rgba(52,211,153,0.06)",
          border: "1px solid rgba(52,211,153,0.32)",
          borderRadius: 6,
        }}
      >
        <div
          className="grid place-items-center"
          style={{
            width: 34, height: 34, borderRadius: 4,
            background: "linear-gradient(160deg, #a78bfa, #8b5cf6)",
          }}
        >
          <ImageIcon size={16} color="#fff" />
        </div>
        <div className="flex-1">
          <div className="text-[12px] text-white/90">presentation.png</div>
          <div className="mono text-[9px] uppercase tracking-[0.22em] text-[#34d399]">
            scanned · routing to → Images/
          </div>
          <div className="h-1 mt-1 bg-white/5 rounded">
            <div className="h-1 rounded" style={{ width: "78%", background: "linear-gradient(120deg, #34d399, #60a5fa)", animation: "hexa-fill 4s ease-in-out infinite" }} />
          </div>
        </div>
        <span className="text-[10px] mono uppercase tracking-[0.22em] text-white/55">2.1 MB</span>
      </div>
      <div className="mt-auto mono text-[9px] uppercase tracking-[0.28em] text-white/30">
        // files · uploads sorted automatically
      </div>
      <style>{`@keyframes hexa-fill { 0% { width: 12%; } 70% { width: 92%; } 100% { width: 12%; } }`}</style>
    </div>
  );
}

/* --------------------------- Frame 4 — Start -------------------------- */
function FrameStart() {
  const sections = [
    { emoji: "📂", label: "Workspace", apps: ["Word", "Excel", "Slides", "PDF"], tint: "#fbbf24" },
    { emoji: "💬", label: "Communication", apps: ["Chat", "Mail", "Meetings"], tint: "#3b82f6" },
    { emoji: "📅", label: "Productivity", apps: ["Tasks", "Calendar"], tint: "#34d399" },
    { emoji: "⚙️", label: "Tools", apps: ["Calculator", "Terminal"], tint: "#a78bfa" },
  ];
  return (
    <div className="absolute inset-0 p-5 flex flex-col gap-2">
      <div
        className="flex items-center gap-2 px-3 py-2 mb-1"
        style={{
          background: "rgba(12,18,36,0.7)",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 24,
        }}
      >
        <Search size={11} className="text-[#a78bfa]" />
        <span className="text-[11px] text-white/55 flex items-center">
          Search apps, settings, files
          <span className="ml-0.5 w-0.5 h-3 bg-[#a78bfa]" style={{ animation: "hexa-caret 1s steps(2) infinite" }} />
        </span>
        <Mic size={11} className="text-white/40 ml-auto" />
      </div>

      {sections.map((s) => (
        <div key={s.label}>
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-sm">{s.emoji}</span>
            <span className="mono text-[9px] uppercase tracking-[0.22em] text-white/55">{s.label}</span>
            <span className="flex-1 h-px bg-white/8" />
            <span className="mono text-[8px] text-white/30">{s.apps.length}</span>
          </div>
          <div className="flex gap-1.5">
            {s.apps.map((a) => (
              <div
                key={a}
                className="flex items-center gap-1.5 px-2 py-1 rounded-md"
                style={{
                  background: `linear-gradient(160deg, ${s.tint}22, ${s.tint}08)`,
                  border: `1px solid ${s.tint}33`,
                }}
              >
                <span
                  className="grid place-items-center"
                  style={{ width: 18, height: 18, borderRadius: 3, background: s.tint, boxShadow: `0 2px 6px ${s.tint}55` }}
                >
                  <Hexagon size={9} color="#fff" />
                </span>
                <span className="text-[10.5px] text-white/85">{a}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="mt-auto mono text-[9px] uppercase tracking-[0.28em] text-white/30">
        // start menu · grouped + searchable
      </div>
    </div>
  );
}
